
import { useState, useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface PinImageUploaderProps {
  onImageChange: (url: string) => Promise<void>;
  userId?: string;
  onImageSelected?: (url: string) => void;
  disabled?: boolean;
}

export function PinImageUploader({ onImageChange, userId, onImageSelected, disabled }: PinImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<{ width: number; height: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Image compression function
  const compressImage = (file: File, maxWidthHeight = 1600, quality = 0.8): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          let { width, height } = img;
          
          // Calculate new dimensions to maintain aspect ratio if image is too large
          if (width > maxWidthHeight || height > maxWidthHeight) {
            if (width > height) {
              height = Math.round(height * (maxWidthHeight / width));
              width = maxWidthHeight;
            } else {
              width = Math.round(width * (maxWidthHeight / height));
              height = maxWidthHeight;
            }
          }
          
          // Save original dimensions
          setImageInfo({ width, height });
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Use webP format for better compression when supported
          const fileType = /jpe?g/.test(file.type) ? 'image/jpeg' : file.type;
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Canvas to Blob conversion failed'));
              }
            }, 
            fileType, 
            quality
          );
        };
        img.onerror = () => reject(new Error('Image loading error'));
      };
      reader.onerror = () => reject(new Error('File reading error'));
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        toast.error("Por favor selecione uma imagem");
        return;
      }
      
      const file = event.target.files[0];
      
      // Check file size first
      if (file.size > 10 * 1024 * 1024) { // > 10MB
        toast.error("Arquivo muito grande. Tamanho máximo: 10MB");
        return;
      }
      
      // Try compressing the image if it's large
      let fileToUpload: File | Blob = file;
      if (file.type.startsWith('image/') && file.size > 1 * 1024 * 1024) {
        try {
          const compressedBlob = await compressImage(file);
          fileToUpload = new File([compressedBlob], file.name, { type: compressedBlob.type });
          
          // If compression didn't help much, use original
          if (compressedBlob.size > file.size * 0.9) {
            fileToUpload = file;
          }
        } catch (error) {
          console.error("Error compressing image:", error);
          fileToUpload = file; // Use original if compression fails
        }
      }
      
      const fileExt = file.name.split('.').pop();
      const filePath = `pins/${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('pins')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data } = supabase.storage
        .from('pins')
        .getPublicUrl(filePath);
        
      const url = data.publicUrl;
      setImageUrl(url);
      
      // Call both callbacks with the URL if they're provided
      if (onImageChange) await onImageChange(url);
      if (onImageSelected) onImageSelected(url);
      
      toast.success("Imagem enviada com sucesso");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setImageUrl(null);
    setImageInfo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageChange) await onImageChange('');
    if (onImageSelected) onImageSelected('');
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <div 
        className="relative flex h-80 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 hover:bg-gray-100"
        onClick={handleUploadClick}
        style={imageInfo ? { aspectRatio: `${imageInfo.width}/${imageInfo.height}` } : {}}
      >
        {imageUrl ? (
          <>
            <img 
              src={imageUrl} 
              alt="Pin" 
              className="h-full w-full rounded-lg object-contain"
              width={imageInfo?.width}
              height={imageInfo?.height}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-800/70 text-white transition-colors hover:bg-gray-900/90"
              disabled={disabled}
              aria-label="Remover imagem"
            >
              <X size={18} />
            </button>
          </>
        ) : (
          <>
            <ImagePlus className="mb-3 h-12 w-12 text-gray-400" aria-hidden="true" />
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Clique para enviar</span> ou arraste uma imagem
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG ou GIF (Max. 10MB)
            </p>
          </>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-primary"></div>
          </div>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        disabled={uploading || disabled}
        className="hidden"
        accept="image/png, image/jpeg, image/gif"
        aria-label="Upload de imagem"
      />
    </div>
  );
}
